import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scrapApi, num, SCRAP_REASON_LABEL, type ProductionOrderScrap } from '@/api/scrap.api';
import { StatusBadge, SourceTag, inr, qtyText } from '@/pages/manufacturing/scrap/components/scrapUi';
import NewScrapModal from '@/pages/manufacturing/scrap/components/NewScrapModal';

// Scrap tab of a production order: what the order scrapped (rejected units sent to scrap stage by
// stage, process waste of its material), what it is worth and how much is disposed. Rejected units not
// yet sent to scrap can be sent from here (the QC disposition).

export default function ScrapTab({ productionOrderId }: { productionOrderId: string }) {
  const navigate = useNavigate();
  const [data, setData] = useState<ProductionOrderScrap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preset, setPreset] = useState<{ productionOrderId: string; stageNumber?: number } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await scrapApi.forProductionOrder(productionOrderId);
      setData(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the scrap of this order');
    } finally {
      setLoading(false);
    }
  }, [productionOrderId]);

  useEffect(() => { void load(); }, [load]);

  if (loading && !data) {
    return <div className="bg-white border border-[#e2e8f0] rounded-xl p-10 text-center text-sm text-[#94a3b8]"><i className="ri-loader-4-line animate-spin" /> Loading scrap…</div>;
  }
  if (error || !data) {
    return <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">{error || 'No data'}</div>;
  }

  const t = data.totals;
  const stagesWithRejections = data.stages.filter((s) => s.rejectedQty > 0);
  const pendingUnits = data.stages.reduce((a, s) => a + s.pendingQty, 0);

  return (
    <div className="space-y-4">
      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total Scrap Value', value: inr(t.scrapValue), sub: `Material cost ${inr(t.bookValue)}` },
          { label: 'Disposed', value: inr(t.disposedValue), sub: 'Sold / destroyed' },
          { label: 'Pending', value: inr(t.pendingValue), sub: 'Waiting approval or in the yard' },
          { label: 'Recovered', value: inr(t.recovery), sub: 'From scrap sales', cls: 'text-green-700' },
          { label: 'Scrap Loss', value: inr(t.loss), sub: 'Cost not recovered', cls: 'text-red-600' },
        ].map((c) => (
          <div key={c.label} className="bg-white border border-[#e2e8f0] rounded-xl p-4">
            <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">{c.label}</p>
            <p className={`text-xl font-bold mt-1 ${c.cls || 'text-[#1e293b]'}`}>{c.value}</p>
            <p className="text-[11px] text-[#94a3b8]">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Rejections stage by stage */}
      <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#e2e8f0]">
          <div>
            <h3 className="text-sm font-semibold text-[#1e293b]">Rejected units by stage</h3>
            <p className="text-xs text-[#94a3b8]">QC disposition: send rejected units to scrap (rework goes through NCR)</p>
          </div>
          {pendingUnits > 0 && (
            <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg">
              {qtyText(pendingUnits)} rejected unit{pendingUnits === 1 ? '' : 's'} not yet dispositioned
            </span>
          )}
        </div>
        {stagesWithRejections.length === 0 ? (
          <p className="px-4 py-6 text-sm text-center text-[#94a3b8]">No rejections recorded on this order's stages</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
              {['Stage', 'Rejected', 'Sent to scrap', 'Left', ''].map((h) => <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748b] uppercase tracking-wide">{h}</th>)}
            </tr></thead>
            <tbody>
              {stagesWithRejections.map((s) => (
                <tr key={s.stageNumber} className="border-b border-[#f1f5f9]">
                  <td className="px-4 py-2.5"><span className="font-semibold text-[#1e293b]">Stage {s.stageNumber}</span> <span className="text-[#64748b]">· {s.stageName}</span></td>
                  <td className="px-4 py-2.5 text-red-600 font-medium">{qtyText(s.rejectedQty)}</td>
                  <td className="px-4 py-2.5">{qtyText(s.scrappedQty)}</td>
                  <td className="px-4 py-2.5 font-semibold">{qtyText(s.pendingQty)}</td>
                  <td className="px-4 py-2.5 text-right">
                    {s.pendingQty > 0 && (
                      <button type="button" onClick={() => setPreset({ productionOrderId: data.productionOrder.id, stageNumber: s.stageNumber })}
                        className="h-7 px-2.5 rounded-md bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 cursor-pointer">
                        <i className="ri-delete-bin-6-line" /> Send to scrap
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Scrap entries of the order */}
      <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#e2e8f0]">
          <h3 className="text-sm font-semibold text-[#1e293b]">Scrap entries ({data.entries.length})</h3>
          <button type="button" onClick={() => navigate(`/inventory/scrap-management?search=${encodeURIComponent(data.productionOrder.po_number)}`)}
            className="text-xs font-semibold text-[#4f46e5] hover:underline cursor-pointer">Open in Scrap Management →</button>
        </div>
        {data.entries.length === 0 ? (
          <p className="px-4 py-6 text-sm text-center text-[#94a3b8]">
            No scrap yet. Process scrap of the issued material is recorded when the order completes (BOM scrap %).
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-max text-sm">
              <thead><tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                {['Scrap No', 'Stage', 'Item', 'Qty', 'Reason', 'Scrap Value', 'Status'].map((h) => <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.entries.map((e) => (
                  <tr key={e.id} className="border-b border-[#f1f5f9]">
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <button type="button" onClick={() => navigate(`/inventory/scrap-management?search=${encodeURIComponent(e.scrap_number)}`)}
                        className="font-mono text-xs font-semibold text-[#4f46e5] hover:underline cursor-pointer">{e.scrap_number}</button>
                      <div className="mt-0.5"><SourceTag source={e.source_type} /></div>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-[#475569]">{e.stage_number != null ? `Stage ${e.stage_number}${e.stage_name ? ` · ${e.stage_name}` : ''}` : '—'}</td>
                    <td className="px-4 py-2.5">{e.item_name}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{qtyText(e.qty)} {e.unit}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-xs text-[#475569]">{SCRAP_REASON_LABEL[e.reason]}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className="font-semibold">{inr(e.scrap_value)}</span>
                      {num(e.sale_amount) > 0 && <span className="block text-[11px] text-green-700">sold {inr(e.sale_amount)}</span>}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap"><StatusBadge status={e.status} method={e.disposal_method} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NewScrapModal
        open={!!preset}
        preset={preset ?? undefined}
        onClose={() => setPreset(null)}
        onCreated={() => { setPreset(null); void load(); }}
      />
    </div>
  );
}
